import React from 'react';
import Style from './sessionsGenerator.module.css';

import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Card, Message, Button, WeezHeader, Icon, Status, ToastContext, Dropdown, ButtonDropdown, DateTimeInput } from '@weezevent/nacre';
import { useForm } from '../../../utils/validators';
import useAppContext from '../../../context/useAppContext';
import SettingsForm from './components/settingsForm';
import RatesForm from './components/ratesForm';
import { useHistory } from 'react-router-dom';
import SessionModel from '../../../models/sessionModel';
import i18n from 'i18next';
import LoadingCard from '../../../components/loadingCard';
import RatesTreeModel from '../../../models/ratesTreeModel';
import QuotasModel from '../../../models/quotasModel';
import { StatusAction } from '../actions/statusAction';
import { SeeAttendeesAction } from '../../rates/components/action/seeAttendeesAction';
import { DeletionModal } from '../actions/deleteAction';

const formatItems = (items, key = 'rate_id') => {
    let _items = {};

    if (!items) {
        return _items;
    }

    items.forEach(i => {
        _items[i[key]] = i.quota ? i.quota.total : null;
    });

    return _items;
};

function HeaderTitle({ session, timezone, locale }) {
    const { t } = useTranslation();

    const dateLocalized = React.useMemo(() => {
        return session
            ? moment.tz(
                  moment
                      .utc(session.start_date, moment.ISO_8601)
                      .tz(timezone)
                      .format('YYYY-MM-DD HH:mm'),
                  timezone
              )
            : null;
    }, [session, timezone]);

    const labelShortDate = React.useMemo(() => {
        if (!dateLocalized) {
            return '';
        }

        let time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(dateLocalized));
        let date = new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(dateLocalized));

        return `${date} • ${time}`;
    }, [dateLocalized]);

    const labelLongDate = React.useMemo(() => {
        if (!dateLocalized) {
            return '';
        }

        let time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(dateLocalized));
        let date = new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(new Date(dateLocalized));

        return `${date} • ${time}`;
    }, [dateLocalized]);

    if (!session) {
        return null;
    }

    return (
        <div className={Style['header-label-container']}>
            <span className={Style['header-date-short']}>{labelShortDate}</span>
            <span className={Style['header-date-long']}>{labelLongDate}</span>
            <Status
                text={session.enabled ? t('ticket.sessions.detail.status.active') : t('ticket.sessions.detail.status.inactive')}
                color={session.enabled ? 'green' : 'grey'}
                responsive
            />
        </div>
    );
}

export default function SessionDetail({ sessionUuid }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const [{ event, organization, event_timezone }] = useAppContext();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [sells, setSells] = React.useState(null);
    const toast = React.useContext(ToastContext);
    const currentYear = new Date().getFullYear();
    const [selectedSession, setSelectedSession] = React.useState([]);

    const scrollTop = React.useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const validateStartDate = React.useCallback((name, date, form) => {
        if (!form.start_date) {
            return t('ticket.sessions.create.dates.error.startDate');
        }
        return true;
    }, []);

    const validateRates = React.useCallback((name, date, form) => {
        if (!Object.keys(form.rates).length) {
            return t('ticket.sessions.create.dates.error.rates');
        }

        return true;
    }, []);

    const validateQuotas = React.useCallback(
        (name, date, form) => {
            const quota = form.quota;
            if (quota !== null && quota < sells?.global) {
                return t('ticket.sessions.detail.rates.quota-error');
            }
            return true;
        },
        [sells]
    );

    const [form, setForm, setFormObject, formErrors, formValidate, setErrors] = useForm(
        {
            start_date: null,
            rates: {},
            rate_groups: {},
            enabled: true,
            quota: null,
            tax_id: null
        },
        {
            start_date: validateStartDate,
            rates: validateRates,
            quota: validateQuotas
        }
    );

    const onChangeForm = React.useCallback(
        (name, value) => {
            setForm(name, value);
            let _errors = formErrors;
            delete _errors[name];
            setErrors(_errors);
        },
        [formValidate, form, setForm, formErrors]
    );

    const onLoadForm = React.useCallback(
        session => {
            setFormObject(
                {
                    start_date: session.start_date,
                    rates: formatItems(session.rates),
                    rate_groups: formatItems(session.rate_groups, 'id'),
                    enabled: session.enabled,
                    quota: session.quota_total,
                    tax_id: session.tax_id
                },
                true
            );
        },
        [setFormObject]
    );

    const [session, loading, sync] = SessionModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            id: sessionUuid,
            query: {
                sync: true
            },
            onSuccess: session => {
                scrollTop();
                onLoadForm(session);
            }
        },
        [organization, event, sessionUuid, onLoadForm, scrollTop]
    );

    const [,] = QuotasModel.useApiModel(
        {
            launch: Boolean(organization && event && session?.weez_date_id),
            organizationId: organization.id,
            eventId: event.id,
            getForRates: true,
            query: {
                id_date: session?.weez_date_id
            },
            onSuccess: _quotas => {
                setSells(
                    _quotas.reduce(
                        (allSells, quota) => {
                            if (quota.rate_group_id > 0) allSells.rate_groups[quota.rate_group_id] = quota.quota_taken;
                            if (quota.rate_id > 0) allSells.rates[quota.rate_id] = quota.quota_taken;
                            if (quota.rate_group_id === 0 && quota.rate_id === 0) allSells.global = quota.quota_taken;
                            return allSells;
                        },
                        { rates: {}, rate_groups: {}, global: 0 }
                    )
                );
            }
        },
        [organization, event, session, setSells]
    );

    const [rates, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk
        },
        [organization, event]
    );

    const totalHierarchyErrors = React.useMemo(() => {
        let errorCount = 0;

        // Check for rates
        for (let rateId in form.rates) {
            const rateQuota = form.rates[rateId];
            const rateSales = sells?.rates[rateId];

            if (rateQuota !== null && rateQuota < rateSales) {
                errorCount++;
            }
        }

        // Check for rate_groups
        for (let rateGroupId in form.rate_groups) {
            const groupQuota = form.rate_groups[rateGroupId];
            const groupSales = sells?.rate_groups[rateGroupId];

            if (groupQuota !== null && groupQuota < groupSales) {
                errorCount++;
            }
        }

        return errorCount;
    }, [form, sells]);

    const isQuotaExceeded = React.useMemo(() => {
        if (!session || !sells) {
            return false;
        }

        if ((form.quota !== null && form.quota < sells.global) || totalHierarchyErrors) {
            return true;
        }

        return false;
    }, [form, session, sells, totalHierarchyErrors]);

    const handleSubmit = React.useCallback(() => {
        if (!formValidate() || isQuotaExceeded) {
            return;
        }

        setIsSubmitting(true);

        let sessionEdited = new SessionModel({ ...session.copy().object, ...form });
        sessionEdited.rates = SessionModel.formatRateQuotas(sessionEdited.rates);
        sessionEdited.rate_groups = SessionModel.formatRateGroupQuotas(sessionEdited.rate_groups);

        sessionEdited
            .save({
                organization,
                event
            })
            .then(response => {
                toast.success(t(`ticket.sessions.detail.toast.success`));
                history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/sessions`);
            })
            .catch(e => {
                console.error(e);
                toast.error(e.response.error === 'duplicated_start_date' ? t(`ticket.sessions.detail.toast.errorDuplicatedDate`) : t(`ticket.sessions.detail.toast.error`));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formValidate, toast, session, form, organization, event, history, isQuotaExceeded]);

    return (
        <div className={Style['session-detail']}>
            <DeletionModal
                onSave={() => history.push(`/ticket/O${organization.id}/E${event.id}/settings/sessions`)}
                onClose={() => setSelectedSession([])}
                itemsToDelete={selectedSession}
                totalItemsSelected={selectedSession.length}
            />
            <WeezHeader
                title={<HeaderTitle session={session} timezone={event_timezone} locale={locale} />}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/sessions`) }}
                rightComponent={[
                    <Button
                        primary
                        key={'submit'}
                        onClick={() => handleSubmit()}
                        icon={<Icon className={Style['cta-save-icon']} name="save" margin={false} />}
                        label={t(`ticket.common.action.save`)}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    />,
                    <ButtonDropdown
                        key={'dropdown'}
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <SeeAttendeesAction key={1} model={SessionModel} filterKey={'booking__inventory__in'} />,
                            <StatusAction key={2} sessionDetail onSave={sync} objects={session ? [session] : null} />,
                            <StatusAction key={3} sessionDetail action={'disable'} objects={session ? [session] : null} onSave={sync} />,
                            <Dropdown.Line key={4} />,
                            <Dropdown.Item
                                key={5}
                                deleteItem
                                disabled={session?.quota_taken > 0}
                                item={t('ticket.common.action.delete')}
                                onClick={() => setSelectedSession([session])}
                            />
                        ]}
                    />
                ]}
            />
            {isQuotaExceeded && (
                <div className={Style['message-danger']}>
                    <Message type="danger">{t('ticket.sessions.detail.rates.message_danger')}</Message>
                </div>
            )}
            <LoadingCard loading={loading}>
                <Card.FormManager useSteps={false}>
                    <Card.FormStep title={t(`ticket.sessions.detail.startDate.title`)} rightComponent>
                        <DateTimeInput
                            className={Style['start_date_input']}
                            timeLabel={t('ticket.event.start_hour')}
                            dateLabel={t('ticket.event.start_date')}
                            onChange={(date, dateObject) => {
                                onChangeForm('start_date', date);
                            }}
                            value={form.start_date}
                            dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15 }}
                            locale={locale}
                            timezone={event_timezone}
                            error={t(formErrors['start_date'])}
                        />
                    </Card.FormStep>
                    <Card.FormStep title={t(`ticket.sessions.detail.settings.title`)} rightComponent>
                        <SettingsForm form={form} onChangeForm={onChangeForm} formErrors={formErrors} globalSells={sells?.global} />
                    </Card.FormStep>
                    <Card.FormStep
                        className={Style['rates-form']}
                        title={
                            <>
                                <div>{t(`ticket.sessions.detail.rates.title`)}</div>
                            </>
                        }
                        rightComponent={
                            <Message type="tips" title={t('ticket.common.tips')} mini>
                                {t('ticket.sessions.detail.rates.tips')}
                            </Message>
                        }
                    >
                        <RatesForm
                            rates={rates}
                            sells={sells}
                            loadingRates={loadingRates}
                            form={form}
                            onChangeForm={onChangeForm}
                            errors={formErrors}
                            totalHierarchyErrors={totalHierarchyErrors}
                            idSession={sessionUuid}
                        />
                    </Card.FormStep>
                </Card.FormManager>
            </LoadingCard>
        </div>
    );
}
